import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMemo } from 'react';

interface MarkdownContentProps {
    content: string;
    className?: string;
}

/**
 * Process text to style @mentions with distinct styling
 * Captures full names (e.g., "@Gaurav Shukla" not just "@Gaurav")
 */
const processMentions = (text: string): React.ReactNode[] => {
    // Regex to match @mentions with full names
    // Stops at: punctuation, space+lowercase (new sentence), or end of string
    const mentionRegex = /@([A-Za-z][A-Za-z0-9\s]*?)(?=[,.:;!?]|\s+[a-z]|\s*$)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
        // Add text before the mention
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        // Add the styled mention
        parts.push(
            <span
                key={match.index}
                className="font-bold text-cyan-400 bg-cyan-400/10 px-1 rounded"
            >
                @{match[1].trim()}
            </span>
        );
        lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : [text];
};

/**
 * Custom text renderer that handles @mentions
 */
const TextWithMentions = ({ children }: { children: React.ReactNode }) => {
    if (typeof children === 'string') {
        return <>{processMentions(children)}</>;
    }
    if (Array.isArray(children)) {
        return (
            <>
                {children.map((child, index) =>
                    typeof child === 'string' ? (
                        <span key={index}>{processMentions(child)}</span>
                    ) : (
                        child
                    )
                )}
            </>
        );
    }
    return <>{children}</>;
};

/**
 * Renders markdown content with proper styling for discussions and comments.
 * Supports GFM (GitHub Flavored Markdown) including tables, lists, code blocks, etc.
 * Also styles @mentions with distinct cyan color.
 */
const MarkdownContent = ({ content, className = '' }: MarkdownContentProps) => {
    return (
        <div className={`markdown-content ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ node, children, ...props }) => (
                        <p className="mb-3 last:mb-0 leading-relaxed text-purple-200/80" {...props}>
                            <TextWithMentions>{children}</TextWithMentions>
                        </p>
                    ),
                    ul: ({ node, ...props }) => (
                        <ul className="list-disc pl-5 mb-3 space-y-1.5 marker:text-purple-400" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                        <ol className="list-decimal pl-5 mb-3 space-y-1.5 marker:text-purple-400" {...props} />
                    ),
                    li: ({ node, children, ...props }) => (
                        <li className="pl-1 leading-relaxed text-purple-200/80" {...props}>
                            <TextWithMentions>{children}</TextWithMentions>
                        </li>
                    ),
                    strong: ({ node, ...props }) => (
                        <strong className="font-bold text-white" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                        <em className="italic text-purple-200" {...props} />
                    ),
                    h1: ({ node, ...props }) => (
                        <h1 className="text-xl font-bold text-white mt-4 mb-2 pb-1 border-b border-white/10" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                        <h2 className="text-lg font-bold text-white mt-3 mb-2" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                        <h3 className="text-base font-bold text-white mt-2 mb-1.5" {...props} />
                    ),
                    blockquote: ({ node, children, ...props }) => (
                        <blockquote className="border-l-4 border-purple-500/50 pl-4 py-1 my-3 italic text-purple-200/70 bg-purple-500/5 rounded-r-xl" {...props}>
                            <TextWithMentions>{children}</TextWithMentions>
                        </blockquote>
                    ),
                    code: ({ node, className, children, ...props }) => {
                        // Check if this is an inline code or code block
                        const isInline = !className?.includes('language-');
                        if (isInline) {
                            return (
                                <code className="bg-white/10 px-1.5 py-0.5 rounded text-purple-200 font-mono text-sm" {...props}>
                                    {children}
                                </code>
                            );
                        }
                        return (
                            <code className={className} {...props}>
                                {children}
                            </code>
                        );
                    },
                    pre: ({ node, ...props }) => (
                        <pre className="bg-black/30 p-3 rounded-xl overflow-x-auto my-3 text-sm font-mono text-purple-100 border border-white/10" {...props} />
                    ),
                    a: ({ node, ...props }) => (
                        <a className="text-purple-400 hover:text-purple-300 underline underline-offset-2" target="_blank" rel="noopener noreferrer" {...props} />
                    ),
                    hr: ({ node, ...props }) => (
                        <hr className="border-white/10 my-4" {...props} />
                    ),
                    // Table components
                    table: ({ node, ...props }) => (
                        <div className="overflow-x-auto my-3 rounded-xl border border-white/10">
                            <table className="w-full text-sm" {...props} />
                        </div>
                    ),
                    thead: ({ node, ...props }) => (
                        <thead className="bg-purple-500/10 text-purple-200" {...props} />
                    ),
                    tbody: ({ node, ...props }) => (
                        <tbody className="divide-y divide-white/10" {...props} />
                    ),
                    tr: ({ node, ...props }) => (
                        <tr className="hover:bg-white/5 transition-colors" {...props} />
                    ),
                    th: ({ node, ...props }) => (
                        <th className="px-3 py-2 text-left font-semibold text-purple-100 text-xs uppercase tracking-wider" {...props} />
                    ),
                    td: ({ node, children, ...props }) => (
                        <td className="px-3 py-2 text-purple-200/80" {...props}>
                            <TextWithMentions>{children}</TextWithMentions>
                        </td>
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownContent;
